import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DollarSign, Save, AlertTriangle } from 'lucide-react';
import { fetchPlanPrices } from '../../services/planPrices';
import api from '../../services/api';

interface UpdatePricesPayload {
  pro_amount_cents: number;
  premium_amount_cents: number;
}

async function updatePlanPrices(payload: UpdatePricesPayload): Promise<void> {
  await api.request<void>('/api/v1/payments/plans/prices', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export const BillingPricesPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [proInput, setProInput]         = useState('');
  const [premiumInput, setPremiumInput] = useState('');
  const [showConfirm, setShowConfirm]   = useState(false);
  const [error, setError]               = useState('');

  const { data: prices, isLoading } = useQuery({
    queryKey: ['plan-prices'],
    queryFn: fetchPlanPrices,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: updatePlanPrices,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan-prices'] });
      setProInput('');
      setPremiumInput('');
      setShowConfirm(false);
      setError('');
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Failed to update prices. Please try again.');
      setShowConfirm(false);
    },
  });

  const validateInputs = (): string | null => {
    const pro        = parseFloat(proInput);
    const premium    = parseFloat(premiumInput);
    const proCents     = Math.round(pro * 100);
    const premiumCents = Math.round(premium * 100);
    if (isNaN(pro) || proCents <= 0)        return 'Pro price must be greater than $0';
    if (isNaN(premium) || premiumCents <= 0) return 'Premium price must be greater than $0';
    if (proCents >= 100_000)                return 'Pro price must be less than $1,000';
    if (premiumCents >= 100_000)            return 'Premium price must be less than $1,000';
    return null;
  };

  const handleSubmitClick = () => {
    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    const proCents     = Math.round(parseFloat(proInput) * 100);
    const premiumCents = Math.round(parseFloat(premiumInput) * 100);
    mutation.mutate({ pro_amount_cents: proCents, premium_amount_cents: premiumCents });
  };

  return (
    <div className="bg-slate-800 rounded-lg p-6 space-y-6">
      <div className="flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-teal-400" />
        <h3 className="text-lg font-semibold text-white">Billing Prices</h3>
      </div>

      {/* Current prices */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-slate-700/50 rounded-lg p-4">
          <p className="text-sm text-slate-400 mb-1">Current Pro Price</p>
          <p className="text-xl font-bold text-white">
            {isLoading ? (
              <span className="inline-block h-6 w-24 bg-slate-600 animate-pulse rounded" />
            ) : (
              prices?.pro?.display ?? '$99.00/mo'
            )}
          </p>
        </div>
        <div className="bg-slate-700/50 rounded-lg p-4">
          <p className="text-sm text-slate-400 mb-1">Current Premium Price</p>
          <p className="text-xl font-bold text-white">
            {isLoading ? (
              <span className="inline-block h-6 w-24 bg-slate-600 animate-pulse rounded" />
            ) : (
              prices?.premium?.display ?? '$199.00/mo'
            )}
          </p>
        </div>
      </div>

      {/* Update form */}
      <div className="space-y-4">
        <p className="text-sm text-slate-400">
          Set new prices below (USD). Changes affect <strong>new subscribers only</strong>.
          Existing subscribers keep their current locked rate.
        </p>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-300 mb-1">New Pro Price (USD/mo)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <input
                type="number"
                min="1"
                max="999"
                step="0.01"
                placeholder="99.00"
                value={proInput}
                onChange={e => setProInput(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-7 pr-3 py-2
                           text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">New Premium Price (USD/mo)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <input
                type="number"
                min="1"
                max="999"
                step="0.01"
                placeholder="199.00"
                value={premiumInput}
                onChange={e => setPremiumInput(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-7 pr-3 py-2
                           text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-400 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> {error}
          </p>
        )}

        <button
          onClick={handleSubmitClick}
          disabled={!proInput || !premiumInput || mutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500
                     disabled:opacity-50 disabled:cursor-not-allowed text-white
                     rounded-lg text-sm font-medium transition-colors"
        >
          <Save className="w-4 h-4" />
          {mutation.isPending ? 'Updating...' : 'Update Prices'}
        </button>
      </div>

      {/* Confirmation dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-600 rounded-xl p-6 max-w-md w-full mx-4 space-y-4">
            <h4 className="text-lg font-semibold text-white">Confirm Price Update</h4>
            <p className="text-sm text-slate-300">
              New prices: <strong className="text-white">Pro ${proInput}/mo</strong>,{' '}
              <strong className="text-white">Premium ${premiumInput}/mo</strong>
            </p>
            <p className="text-sm text-amber-400">
              This will affect new subscribers only. Existing subscribers keep their
              current locked rate.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleConfirm}
                disabled={mutation.isPending}
                className="flex-1 py-2 bg-teal-600 hover:bg-teal-500 text-white
                           rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {mutation.isPending ? 'Updating...' : 'Confirm Update'}
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                disabled={mutation.isPending}
                className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-white
                           rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
